import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Mixins, Ref, Watch } from 'vue-property-decorator';
import { SUBSTITUTE_TEACHER_STORE } from '@/tools/store/substitute-teacher.store';
import { SubstituteTeacherService } from '@/tools/services/substitute-teacher.service';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import { INVITATION_TYPE, INVITATION_STATUS } from '@/tools/values/substitute-teacher.values';
import SessionMixin from '@/core/mixins/session.mixin';
import { APP_EVENT_BUS } from '@/core/enums/app.enum';

@Component({
  name: 'ed-list-substitute-teacher'
})
export default class EdListSubstituteTeacherComponent extends Mixins (SessionMixin) {

  /* Props */
  @Prop() readonly orgId: number;
  @Prop({default: INVITATION_TYPE.GIVEN}) readonly tab: string;

  @Ref('dataTable') readonly dataTable: any;

   /* Data */
   public loading: boolean = true
   public givenPagination: any = { current: 1, total: 0 }
   public receivedPagination: any = { current: 1, total: 0 }
   public tabs = INVITATION_TYPE
   public invitationStatus = INVITATION_STATUS;
 
   
   /* Computed */
   get  headers() { 
     let headers = [
       { text:  this.$t('tools.substituteTeacher.headers.teacherName'), align: 'start', value: 'fullName', sortable: false, show: true },
       { text: this.$t('tools.substituteTeacher.headers.from'), value: 'startDate', sortable: false, show: true },
       { text: this.$t('tools.substituteTeacher.headers.to'), value: 'endDate', sortable: false, show: true },
       { text: this.$t('tools.substituteTeacher.headers.email'), value: 'substituteEmail', sortable: false, show: this.tab == this.tabs.GIVEN },
       { text: this.$t('tools.substituteTeacher.headers.classes'), value: 'classNames', sortable: false,  show: true },
       { text: this.$t('tools.substituteTeacher.headers.status'), value: 'status', sortable: false, show: true },
       { text: this.$t('tools.substituteTeacher.headers.actions'), value: 'actions', sortable: false, show: true }
     ].filter(el => el.show)
     return headers
   }
 
   get givenInvitations() {
     return this.$store.state.SubstituteTeacherStore.givenInvitations; 
   }
 
   get receivedInvitations() {
     return this.$store.state.SubstituteTeacherStore.receivedInvitations;
   }
 
   get tableItems() {
     let data;
     if(this.tab === this.tabs.GIVEN)
        data = this.getGivenInvitations;
      else 
        data = this.getReceivedInvitations;
      
     return data
   }
 
   get getGivenInvitations() {
     this.loading = true;
     
      // Check with store if the page was loaded previously    
     let givenInvitations = this.givenInvitations?.find(el => el.page === this.givenPagination['current']-1)?.content;
 
     // Fetch new page
     if (!givenInvitations) {
      this.$store.dispatch(`${SUBSTITUTE_TEACHER_STORE.ACTIONS.GET_SUBSTITUTE_TEACHER_A}`,{
          type: INVITATION_TYPE.GIVEN,
          page: this.givenPagination['current']-1, 
          pageSize: 10
       }).then((res) => { 
          this.givenPagination['total'] = res.totalPages;
          this.loading = false;
          return res.content;
       }).catch((err) => {
          this.loading = false;
       })
     } else this.loading = false;
     return givenInvitations
   }
 
   get getReceivedInvitations() {
     this.loading = true;
     
      // Check with store if the page was loaded previously    
     let receivedInvitations = this.receivedInvitations?.find(el => el.page === this.receivedPagination['current']-1)?.content;
 
     // Fetch new page
     if (!receivedInvitations) {
      this.$store.dispatch(`${SUBSTITUTE_TEACHER_STORE.ACTIONS.GET_SUBSTITUTE_TEACHER_A}`,{
          type: INVITATION_TYPE.RECEIVED,
          page: this.receivedPagination['current']-1, 
          pageSize: 10
       }).then((res) => { 
          this.receivedPagination['total'] = res.totalPages;
          this.loading = false;
          return res.content;
       }).catch(() => {
          this.loading = false;
       })
     } else this.loading = false;
     return receivedInvitations
   }

  get dataTableCustomStyle(): string {
    return 'border-radius: inherit;';
  }

   /* Methods */
   getClassNames(items) {
    return items.classNames.map(el => el.name).join(', ');
   }

   translateStatus(status: string) {
    return this.$t(`tools.substituteTeacher.status.${status.toLowerCase()}`)
   }
 
   updateInvitation (item: any) {
    this.$edEventBus.$emit('openEditInvitation', item);
   }
 
   deleteInvitation(item: any) {
    this.$EdModal.createConfirmationModal(
      {
        body: this.$t('tools.substituteTeacher.info.deleteInvitationConfirm'), 
        action: async () => {
          await this.doDeleteInvitation(item);
        }
      }
    );
   }

   doDeleteInvitation(item: any) {
    SubstituteTeacherService.deleteTeacherInvitation(item.substituteTeacherId)
    .then(() => {
      this.resetInvitations();
      this.$EdToast.create({ 
        msg:  `${this.$t('tools.substituteTeacher.success.invitationDeleted')}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
    }).catch(() => {
      this.$EdToast.create({ 
        msg:  `${this.$t('tools.substituteTeacher.errors.invitationNoDeleted')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    })
   }

   acceptInvitation(item: any) {
    let status = this.invitationStatus.ACCEPTED;
    SubstituteTeacherService.acceptRejectTeacherInvitation(status, item.substituteTeacherId)
    .then(() => {
      this.initInstitution();
      this.resetInvitations();
      this.$EdToast.create({ 
        msg:  `${this.$t('tools.substituteTeacher.success.invitationAccepted')}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
    }).catch(() => {
      this.$EdToast.create({ 
        msg:  `${this.$t('tools.substituteTeacher.errors.invitationNoAccepted')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    })
   }

   rejectInvitation(item: any) {
    this.$EdModal.createConfirmationModal(
      {
        body: this.$t('tools.substituteTeacher.info.rejectInvitationConfirm'), 
        action: async () => {
          await this.doRejectInvitation(item);
        }
      }
    );
   }
 
   doRejectInvitation (item: any) {
    let status = this.invitationStatus.REJECTED;
    SubstituteTeacherService.acceptRejectTeacherInvitation(status, item.substituteTeacherId)
      .then(() => {
        this.resetInvitations();
        this.$EdToast.create({ 
          msg:  `${this.$t('tools.substituteTeacher.success.invitationRejected')}`,
          type: TOAST_TYPES.SUCCESS,
          top: '3rem',
          timeout: 3000
        });
      }).catch(() => {
        this.$EdToast.create({ 
          msg:  `${this.$t('tools.substituteTeacher.errors.invitationNoRejected')}`,
          type: TOAST_TYPES.DANGER,
          top: '3rem',
          timeout: 3000
        });
      })
   }

   resetInvitations() {
    this.$store.commit(SUBSTITUTE_TEACHER_STORE.MUTATIONS.RESET_TEACHER_INVITATIONS_M);
   }
 
   destroyed() {
     this.resetInvitations();
   }
 
  @Watch('tableItems')
  private _watchTableItems(): void {
    this._loadInvitationsInDataTable();
  }

   public onPageUpdate(pagination: { current: number, total: number }, customEvent: CustomEvent): void {
    const detail: { page: number } = customEvent.detail;
    pagination.current = detail.page;
    this._loadInvitationsInDataTable();
  }

  private _onLanguageChanged(lang: string): void {
    this.$nextTick(() => {
      this.dataTable?.setAttribute('lang', lang);
      this._loadInvitationsInDataTable();
    });
  }

  private _loadInvitationsInDataTable(): void {
    if (!this.dataTable || !this.dataTable.loadData) {
      return;
    }

    if (!this.tableItems || this.tableItems.length == 0) {
      this.dataTable.loadData({ items: [], headers: this.headers });
      return;
    }

    const slots = {
      'item.fullName': {
        template: document.querySelector('#item\\.fullName').innerHTML,
        templateParams: [{
          key: 'item.fullName',
          value: (params) => {
            const fullName = params.item.fullName;
            return !!fullName && fullName != null ? fullName : '';
          }
        }],
      },
      'item.startDate': {
        template: document.querySelector('#item\\.startDate').innerHTML,
        templateParams: [{
          key: 'item.startDate',
          value: (params) => params.item.startDate,
        }],
      },
      'item.endDate': {
        template: document.querySelector('#item\\.endDate').innerHTML,
        templateParams: [{
          key: 'item.endDate',
          value: (params) => params.item.endDate,
        }],
      },
      'item.classNames': {
        template: document.querySelector('#item\\.classNames').innerHTML,
        templateParams: [{
          key: 'item.classNames',
          value: (params) => params.parent.getClassNames(params.item),
        }],
      },
      'item.status': {
        template: document.querySelector('#item\\.status').innerHTML,
        templateParams: [{
          key: 'item.status',
          value: (params) => params.parent.translateStatus(params.item.status),
        }],
      },
      'item.actions': {
        template: document.querySelector('#item\\.actions').innerHTML,
        templateParams: [{}],
        expressions: [
          {
            target: '.acceptReceivedInvitation', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.item.status === params.parent.invitationStatus.INVITED,
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.rejectReceivedInvitation', attribute: 'style', booleanAttribute: false,
            expression: (params) => params.item.status === params.parent.invitationStatus.INVITED,
            applied: 'display: inline;', fallback: 'display: none;'
          },
        ],
        events: [
          { target: '.editGivenInvitation', handler: (params) => params.parent.updateInvitation(params.item) },
          { target: '.removeGivenInvitation', handler: (params) => params.parent.deleteInvitation(params.item) },
          { target: '.acceptReceivedInvitation', handler: (params) => params.parent.acceptInvitation(params.item) },
          { target: '.rejectReceivedInvitation', handler: (params) => params.parent.rejectInvitation(params.item) },
        ],
      },
      parent: this,
    };

    this.dataTable.loadData({
      items: this.tableItems,
      headers: this.headers,
      slots: slots,
    });
  }

  /* Hooks */
  mounted() {
    this.$edEventBus.$on(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
    this._loadInvitationsInDataTable();
  }

  beforeDestroy() {
    this.$edEventBus.$off(APP_EVENT_BUS.LANGUAGE_CHANGED, this._onLanguageChanged);
  }
}